import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import courses from '../data/courses';

export default function Courses() {
  const [selectedLevel, setSelectedLevel] = useState('Tümü');
  const levels = ['Tümü', ...new Set(courses.map(c => c.level))];

  const filteredCourses = selectedLevel === 'Tümü' 
    ? courses 
    : courses.filter(c => c.level === selectedLevel);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <motion.h1 
             initial={{ opacity: 0, scale: 0.9 }}
             animate={{ opacity: 1, scale: 1 }}
             transition={{ duration: 0.5 }}
             className="text-5xl md:text-6xl font-serif font-bold text-navy mb-4 tracking-tight"
          >
            Dersler
          </motion.h1>
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: "6rem" }}
            transition={{ duration: 0.8 }}
            className="h-1 bg-gradient-to-r from-transparent via-navy to-transparent mx-auto mb-4"
          ></motion.div>
        </div>

        {/* Filter Buttons */}
        <div className="mb-10 flex flex-wrap gap-3 justify-center">
          {levels.map((level) => (
            <motion.button
              key={level}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedLevel(level)}
              className={`px-6 py-2.5 rounded-lg transition-colors duration-300 font-medium ${
                selectedLevel === level
                  ? 'bg-navy text-white shadow-lg'
                  : 'bg-white text-navy border-2 border-gray-300 hover:border-navy hover:bg-navy hover:text-white'
              }`}
            >
              {level}
            </motion.button>
          ))}
        </div>

        {/* Courses List */}
        <motion.div layout className="space-y-8">
          <AnimatePresence mode='popLayout'>
            {filteredCourses.map((course) => (
              <motion.div
                layout
                key={course.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow duration-300"
              >
                {/* Header Section */}
                <div className="mb-6">
                  <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-serif font-bold text-navy mb-3">
                        {course.name}
                      </h3>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-2">
                        <span className="px-3 py-1 bg-gray-100 rounded-lg font-medium">Kod: {course.code}</span>
                        {course.credits && <span className="px-3 py-1 bg-gray-100 rounded-lg">{course.credits}</span>}
                        {course.year && <span className="px-3 py-1 bg-gray-100 rounded-lg">{course.year}</span>}
                      </div>
                      {course.department && (
                        <p className="text-gray-600 mt-2">
                          {course.department} {course.classLevel && `• ${course.classLevel}`} {course.type && `• ${course.type}`}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="px-4 py-1.5 bg-navy text-white text-xs font-semibold rounded-lg text-center">
                        {course.level}
                      </span>
                      <span className="px-4 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-lg text-center">
                        📅 {course.semester} Dönemi
                      </span>
                    </div>
                  </div>
                  
                  {course.topic && (
                    <p className="text-gray-700 font-medium mb-2">
                      Konu: {course.topic}
                    </p>
                  )}
                  
                  <p className="text-gray-700 leading-relaxed">{course.description}</p>
                </div>

                {/* Textbook Section */}
                {course.textbook && (
                  <div className="mb-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">📖</span>
                      Ders Kitabı
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-800 font-semibold mb-1">
                        {course.textbook.title} <span className="text-gray-600 font-normal">({course.textbook.edition})</span>
                      </p>
                      <p className="text-gray-700 mb-1">
                        {course.textbook.authors} <span className="text-gray-600">({course.textbook.year})</span>
                      </p>
                      <p className="text-gray-600 text-sm">{course.textbook.publisher}</p>
                    </div>
                  </div>
                )}

                {/* Reference Books Section */}
                {course.referenceBooks && course.referenceBooks.length > 0 && (
                  <div className="mb-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">📚</span>
                      Yardımcı Kitaplar
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      {course.referenceBooks.map((book, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-navy mt-1">•</span>
                          <span>{book}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Prerequisites Section */}
                {(course.prerequisites || course.additionalRequirements) && (
                  <div className="mb-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">⚠️</span>
                      Önkoşullar ve Gereksinimler
                    </h4>
                    {course.prerequisites && course.prerequisites.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Önkoşul:</p>
                        <div className="flex flex-wrap gap-2">
                          {course.prerequisites.map((req, index) => (
                            <span key={index} className="px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium rounded-lg">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {course.additionalRequirements && course.additionalRequirements.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Ek Koşullar:</p>
                        <div className="flex flex-wrap gap-2">
                          {course.additionalRequirements.map((req, index) => (
                            <span key={index} className="px-3 py-1.5 bg-orange-50 border border-orange-200 text-orange-800 text-xs font-medium rounded-lg">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Software Section */}
                {course.software && course.software.length > 0 && (
                  <div className="mb-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">💻</span>
                      Kullanılacak Yazılımlar
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {course.software.map((sw, index) => (
                        <span key={index} className="px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-sm font-medium rounded-lg">
                          {sw}
                        </span>
                      ))}
                    </div>
                    {course.softwareLinks && course.softwareLinks.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Yazılım İndirme:</p>
                        {course.softwareLinks.map((link, index) => (
                          <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-navy hover:underline mr-4 inline-block"
                          >
                            {link.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Weekly Schedule Section */}
                {course.weeklySchedule && course.weeklySchedule.length > 0 && (
                  <div className="mb-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">📅</span>
                      Haftalık Ders Akışı
                    </h4>
                    <ol className="space-y-2 text-gray-700">
                      {course.weeklySchedule.map((week, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                            {index + 1}
                          </span>
                          <span className="pt-0.5">{week}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Materials Section */}
                {course.materials && course.materials.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                      <span className="text-xl">📎</span>
                      Materyaller
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {course.materials.map((material, index) => (
                        <motion.a
                          key={index}
                          href={material.url}
                          target={material.url !== '#' ? '_blank' : undefined}
                          rel={material.url !== '#' ? 'noopener noreferrer' : undefined}
                          whileHover={{ scale: 1.05, backgroundColor: "#1a237e" }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition-colors text-sm font-medium shadow-sm"
                        >
                          {material.type}
                        </motion.a>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}